<template>
  <div class="profile-contact-editor">
    <!-- Contact information display -->
    <v-card v-if="showCard" class="mb-6 pa-6" elevation="1">
      <h-info-display :items="contactInfoItems" />
    </v-card>
    <h-info-display v-else :items="contactInfoItems" />

    <!-- Dialog for editing email -->
    <h-action-dialog
      v-model="emailDialogVisible"
      :title="useHTranslate('email_dialog_title')"
      dialog-width="600"
      :save-loading="emailSaveLoading"
      :save-disabled="isEmailUnchanged"
      save-icon="mdi:mdi-content-save"
      :class="childDialogClass"
      @save="saveEmail"
      @close="emailDialogVisible = false"
    >
      <v-alert color="info" variant="tonal" class="mb-4 text-body-2">
        {{ $t('citizen_my_profile_email_dialog_info') }}
      </v-alert>
      <h-form-text
        v-model="editedEmail"
        :label="useHTranslate('email_label')"
        variant="filled"
        :rules="[hValidate.required, hValidate.email]"
        required
      />
    </h-action-dialog>

    <!-- Dialog for changing phone number -->
    <h-action-dialog
      v-model="phoneDialogVisible"
      :title="useHTranslate('phone_dialog_title')"
      dialog-width="640"
      :save-loading="phoneSaveLoading"
      :save-disabled="isPhoneUnchanged"
      save-icon="mdi:mdi-content-save"
      :class="childDialogClass"
      @save="savePhone"
      @close="phoneDialogVisible = false"
    >
      <v-alert color="info" variant="tonal" class="mb-4 text-body-2">
        {{ $t('citizen_my_profile_phone_dialog_info') }}
      </v-alert>
      <h-form-text v-model="editedPhone" :label="useHTranslate('phone_label')" variant="filled" :rules="phoneRules()" required />
    </h-action-dialog>

    <!-- Bank details are saved without a verification step. -->
    <h-action-dialog
      v-model="bankAccountDialogVisible"
      :title="useHTranslate('citizen_my_profile_bank_account_dialog_title')"
      dialog-width="640"
      :save-loading="bankAccountSaveLoading"
      :save-disabled="isBankAccountUnchanged || !isValidBankAccount(editedBankAccount)"
      save-icon="mdi:mdi-content-save"
      :class="childDialogClass"
      @save="saveBankAccount"
      @close="bankAccountDialogVisible = false"
    >
      <v-alert color="info" variant="tonal" class="mb-4 text-body-2">
        {{ $t('citizen_my_profile_bank_account_dialog_info') }}
      </v-alert>
      <h-form-text
        v-model="editedBankAccount"
        :label="useHTranslate('citizen_my_profile_bank_account_label')"
        variant="filled"
        type="text"
        :rules="[bankAccountRule]"
        :disabled="bankAccountSaveLoading"
      />
    </h-action-dialog>

    <!-- Dialog for phone verification code -->
    <h-action-dialog
      v-model="phoneVerificationDialogVisible"
      :title="$t('citizen_my_profile_enter_code')"
      dialog-width="640"
      :save-text="
        hasPhoneProcessError || verificationStatus.mobile_phone.noMoreAttempts
          ? $t('citizen_my_profile_send_new_code')
          : $t('citizen_my_profile_verify')
      "
      :close-text="$t('citizen_cancel')"
      :save-icon="hasPhoneProcessError || verificationStatus.mobile_phone.noMoreAttempts ? 'mdi:mdi-refresh' : 'mdi:mdi-check'"
      :save-loading="phoneVerificationLoading"
      :class="childDialogClass"
      @save="handlePhoneSaveClick"
      @close="closePhoneVerificationDialog"
    >
      <!-- Process Error Alert -->
      <v-alert v-if="verificationStatus.mobile_phone.processError" color="error" variant="tonal" class="mb-4 text-body-2">
        <v-icon class="mr-2">mdi:mdi-alert-circle</v-icon>
        {{ useHTranslate(`verification_error_${verificationStatus.mobile_phone.processError.toLowerCase()}`) }}
      </v-alert>

      <!-- Success Alert -->
      <v-alert v-else-if="verificationStatus.mobile_phone.verified === true" color="success" variant="tonal" class="mb-4 text-body-2">
        <v-icon class="mr-2">mdi:mdi-check-circle</v-icon>
        {{ useHTranslate('verification_phone_success') }}
      </v-alert>

      <!-- No More Attempts Alert -->
      <v-alert v-else-if="verificationStatus.mobile_phone.noMoreAttempts" color="error" variant="tonal" class="mb-4 text-body-2">
        <v-icon class="mr-2">mdi:mdi-alert-circle</v-icon>
        {{ useHTranslate('verification_no_more_attempts') }}
      </v-alert>

      <!-- Remaining Attempts Warning -->
      <v-alert
        v-else-if="
          verificationStatus.mobile_phone.numberOfAttemptsLeft !== null && verificationStatus.mobile_phone.numberOfAttemptsLeft <= 2
        "
        color="warning"
        variant="tonal"
        class="mb-4 text-body-2"
      >
        <v-icon class="mr-2">mdi:mdi-alert</v-icon>
        {{ useHTranslate('verification_attempts_remaining', { count: verificationStatus.mobile_phone.numberOfAttemptsLeft }) }}
      </v-alert>

      <!-- Info Alert (default) -->
      <v-alert v-else color="info" variant="tonal" class="mb-4 text-body-2">
        {{ useHTranslate('verification_phone_instruction') }}
      </v-alert>

      <!-- OTP Input -->
      <h-form-otp-input
        v-if="!hasPhoneProcessError"
        v-model="phoneOtpCode"
        :length="6"
        class="mb-2"
        :disabled="verificationStatus.mobile_phone.noMoreAttempts || verificationStatus.mobile_phone.verified"
        @complete="handlePhoneOtpComplete"
      />

      <!-- Helper Text with Resend Link -->
      <div
        v-if="!hasPhoneProcessError && !verificationStatus.mobile_phone.noMoreAttempts && !verificationStatus.mobile_phone.verified"
        class="text-caption mb-3 mt-1"
      >
        {{ useHTranslate('verification_no_code') }}
        <button type="button" class="font-weight-bold profile-resend-code" @click="resendPhoneCode">
          {{ useHTranslate('verification_resend_code') }}
        </button>
      </div>

      <!-- Retry Info -->
      <div v-if="hasPhoneProcessError || verificationStatus.mobile_phone.noMoreAttempts" class="text-caption mb-3 mt-1">
        {{ useHTranslate('verification_send_new_code_info') }}
      </div>

      <!-- Timer -->
      <div
        v-if="!hasPhoneProcessError && !verificationStatus.mobile_phone.noMoreAttempts && !verificationStatus.mobile_phone.verified"
        class="text-caption mb-0"
      >
        <span>{{ useHTranslate('verification_phone_time_remaining') }}:</span>
        <h-countdown-timer
          ref="phoneTimerRef"
          :seconds="phoneTimeRemaining"
          :show-hours="false"
          class="font-weight-medium"
          @complete="handlePhoneTimerComplete"
        />
      </div>
    </h-action-dialog>

    <!-- Dialog for email verification code -->
    <h-action-dialog
      v-model="verificationDialogVisible"
      :title="$t('citizen_my_profile_enter_code')"
      dialog-width="640"
      :save-text="
        hasEmailProcessError || verificationStatus.email.noMoreAttempts
          ? $t('citizen_my_profile_send_new_code')
          : $t('citizen_my_profile_verify')
      "
      :close-text="$t('citizen_cancel')"
      :save-icon="hasEmailProcessError || verificationStatus.email.noMoreAttempts ? 'mdi:mdi-refresh' : 'mdi:mdi-check'"
      :save-loading="emailVerificationLoading"
      :class="childDialogClass"
      @save="handleEmailSaveClick"
      @close="closeEmailVerificationDialog"
    >
      <!-- Process Error Alert -->
      <v-alert v-if="verificationStatus.email.processError" color="error" variant="tonal" class="mb-4 text-body-2">
        <v-icon class="mr-2">mdi:mdi-alert-circle</v-icon>
        {{ useHTranslate(`verification_error_${verificationStatus.email.processError.toLowerCase()}`) }}
      </v-alert>

      <!-- Success Alert -->
      <v-alert v-else-if="verificationStatus.email.verified === true" color="success" variant="tonal" class="mb-4 text-body-2">
        <v-icon class="mr-2">mdi:mdi-check-circle</v-icon>
        {{ useHTranslate('verification_email_success') }}
      </v-alert>

      <!-- No More Attempts Alert -->
      <v-alert v-else-if="verificationStatus.email.noMoreAttempts" color="error" variant="tonal" class="mb-4 text-body-2">
        <v-icon class="mr-2">mdi:mdi-alert-circle</v-icon>
        {{ useHTranslate('verification_no_more_attempts') }}
      </v-alert>

      <!-- Remaining Attempts Warning -->
      <v-alert
        v-else-if="verificationStatus.email.numberOfAttemptsLeft !== null && verificationStatus.email.numberOfAttemptsLeft <= 2"
        color="warning"
        variant="tonal"
        class="mb-4 text-body-2"
      >
        <v-icon class="mr-2">mdi:mdi-alert</v-icon>
        {{ useHTranslate('verification_attempts_remaining', { count: verificationStatus.email.numberOfAttemptsLeft }) }}
      </v-alert>

      <!-- Info Alert (default) -->
      <v-alert v-else color="info" variant="tonal" class="mb-4 text-body-2">
        {{ useHTranslate('verification_code_instruction') }}
      </v-alert>

      <!-- OTP Input -->
      <h-form-otp-input
        v-if="!hasEmailProcessError"
        v-model="otpCode"
        :length="6"
        class="mb-2"
        :disabled="verificationStatus.email.noMoreAttempts || verificationStatus.email.verified"
        @complete="handleOtpComplete"
      />

      <!-- Helper Text with Resend Link -->
      <div
        v-if="!hasEmailProcessError && !verificationStatus.email.noMoreAttempts && !verificationStatus.email.verified"
        class="text-caption mb-3 mt-1"
      >
        {{ useHTranslate('verification_no_code') }}
        <button type="button" class="font-weight-bold profile-resend-code" @click="resendCode">
          {{ useHTranslate('verification_resend_code') }}
        </button>
      </div>

      <!-- Retry Info -->
      <div v-if="hasEmailProcessError || verificationStatus.email.noMoreAttempts" class="text-caption mb-3 mt-1">
        {{ useHTranslate('verification_send_new_code_info') }}
      </div>

      <!-- Timer -->
      <div
        v-if="!hasEmailProcessError && !verificationStatus.email.noMoreAttempts && !verificationStatus.email.verified"
        class="text-caption mb-0"
      >
        <span>{{ useHTranslate('verification_time_remaining') }}:</span>
        <h-countdown-timer
          ref="timerRef"
          :seconds="timeRemaining"
          :show-hours="false"
          class="font-weight-medium"
          @complete="handleTimerComplete"
        />
      </div>
    </h-action-dialog>
  </div>

  <!-- Fullscreen loader for custom process (e.g. TOPdesk sync + case indexing) -->
  <h-loader :is-loading="customProcessLoading" :text="useHTranslate('citizen_loading_cases')" color="primary" text-color="on-primary" />
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import {
  useFormRules,
  useHAlerts,
  useHTranslate,
  useOUserSettingsStore,
  useOCommon,
  useOSettings,
  useHCommon,
  hValidate,
} from '@onify/helix-core';
import { isValidBankAccount } from '../utils/contact-details.js';

const props = defineProps({
  /**
   * Object of suggested values keyed by field name (e.g., { email: 'new@email.com', mobile_phone: '+46...' }).
   * When provided, these values override the displayed value in the contact info list.
   * Email and phone suggestions can be verified; bank details can only be saved.
   */
  suggestedValues: {
    type: Object,
    default: () => ({}),
  },
  /**
   * Whether to show the card wrapper. Set to false when used inside a modal.
   */
  showCard: {
    type: Boolean,
    default: true,
  },
  /**
   * Whether child dialogs should use elevated z-index (when used inside profile modal).
   */
  elevated: {
    type: Boolean,
    default: false,
  },
  /**
   * Array of field keys that are required (will be marked with "*" in the label).
   */
  requiredFields: {
    type: Array,
    default: () => [],
  },
  /**
   * Displays stored values without exposing edit, verify, or confirm actions.
   */
  readOnly: {
    type: Boolean,
    default: false,
  },
});

// Computed class for child dialogs that need elevated z-index
const childDialogClass = computed(() => (props.elevated ? 'profile-child-dialog--elevated' : undefined));

// Core dependencies
const { hHttpRequest } = useHCommon();
const { hAddAlert } = useHAlerts();
const { citizen_my_profile } = useOSettings();
const { phoneRules } = useFormRules();

// User data
const userSettingsStore = useOUserSettingsStore();
const { oRefetchUserSettings } = userSettingsStore;
const { oUser } = useOCommon();

// --- State ---

const createDefaultVerificationState = () => ({
  verified: null,
  numberOfAttemptsLeft: null,
  hasMoreAttempts: true,
  noMoreAttempts: false,
  isChecking: false,
  error: null,
  processError: null,
});

// Dialog states
const emailDialogVisible = ref(false);
const editedEmail = ref('');
const originalEmail = ref('');
const phoneDialogVisible = ref(false);
const editedPhone = ref('');
const originalPhone = ref('');
const bankAccountDialogVisible = ref(false);
const editedBankAccount = ref('');
const originalBankAccount = ref('');

// Track fields that have been confirmed locally (optimistic UI update)
const confirmedFields = ref(new Set());

// Loading states
const emailSaveLoading = ref(false);
const phoneSaveLoading = ref(false);
const bankAccountSaveLoading = ref(false);
const emailVerificationLoading = ref(false);
const phoneVerificationLoading = ref(false);
const customProcessLoading = ref(false);

// Verification dialog states
const verificationDialogVisible = ref(false);
const otpCode = ref('');
const timeRemaining = ref(300);
const timerRef = ref(null);
const phoneVerificationDialogVisible = ref(false);
const phoneOtpCode = ref('');
const phoneTimeRemaining = ref(300);
const phoneTimerRef = ref(null);

watch(
  () => props.readOnly,
  (readOnly) => {
    if (!readOnly) return;
    emailDialogVisible.value = false;
    phoneDialogVisible.value = false;
    bankAccountDialogVisible.value = false;
  },
);

// Computed
const isEmailUnchanged = computed(() => editedEmail.value === originalEmail.value);
const isPhoneUnchanged = computed(() => editedPhone.value === originalPhone.value);
const isBankAccountUnchanged = computed(
  () => typeof editedBankAccount.value === 'string' && editedBankAccount.value.trim() === originalBankAccount.value,
);
const bankAccountRule = (value) => isValidBankAccount(value) || useHTranslate('citizen_my_profile_bank_account_invalid');

// localStorage keys
const VERIFICATION_PROCESS_KEY_PREFIX = 'verification_process_';

// Process IDs
const verificationProcessIds = ref({
  email: '',
  mobile_phone: '',
});

// Verification status per type
const verificationStatus = ref({
  email: createDefaultVerificationState(),
  mobile_phone: createDefaultVerificationState(),
});

// Computed for process errors
const hasEmailProcessError = computed(() => !!verificationStatus.value.email.processError);
const hasPhoneProcessError = computed(() => !!verificationStatus.value.mobile_phone.processError);

// --- localStorage helpers ---

const getStoredProcessId = (type) => {
  try {
    const stored = localStorage.getItem(`${VERIFICATION_PROCESS_KEY_PREFIX}${type}`);
    if (stored) {
      const data = JSON.parse(stored);
      if (data.expireAt && new Date(data.expireAt) > new Date()) {
        return data;
      }
      localStorage.removeItem(`${VERIFICATION_PROCESS_KEY_PREFIX}${type}`);
    }
  } catch (e) {
    console.error('Error reading from localStorage:', e);
  }
  return null;
};

const storeProcessId = (type, processId, expireAt) => {
  try {
    localStorage.setItem(
      `${VERIFICATION_PROCESS_KEY_PREFIX}${type}`,
      JSON.stringify({
        processId,
        expireAt,
        createdAt: new Date().toISOString(),
      }),
    );
  } catch (e) {
    console.error('Error writing to localStorage:', e);
  }
};

const clearStoredProcessId = (type) => {
  try {
    localStorage.removeItem(`${VERIFICATION_PROCESS_KEY_PREFIX}${type}`);
  } catch (e) {
    console.error('Error removing from localStorage:', e);
  }
};

// --- Verification helpers ---

const setVerificationTimer = (type, expireAt) => {
  if (!expireAt) return;
  const now = new Date();
  const expireDate = new Date(expireAt);
  const remainingSeconds = Math.max(0, Math.floor((expireDate - now) / 1000));

  if (type === 'email') {
    timeRemaining.value = remainingSeconds;
    if (timerRef.value) timerRef.value.restart();
  } else {
    phoneTimeRemaining.value = remainingSeconds;
    if (phoneTimerRef.value) phoneTimerRef.value.restart();
  }
};

const openVerificationDialogForType = (type) => {
  if (type === 'email') {
    verificationDialogVisible.value = true;
  } else {
    phoneVerificationDialogVisible.value = true;
  }
};

const prepareVerificationValue = (type, value) => {
  if (type === 'email') {
    editedEmail.value = value;
    originalEmail.value = value;
    otpCode.value = '';
  } else {
    editedPhone.value = value;
    originalPhone.value = value;
    phoneOtpCode.value = '';
  }
};

const fetchProcessExpireAt = async (processId) => {
  try {
    const { response } = hHttpRequest({
      url: `my/processes/${processId}`,
      method: 'get',
    });
    const res = await (await response()).json();
    return res.expireat || null;
  } catch (error) {
    console.error('Error fetching process expireAt:', error);
    return null;
  }
};

const checkProcessStatus = async (type) => {
  const processId = verificationProcessIds.value[type];
  if (!processId) return { ok: true };

  try {
    const { response } = hHttpRequest({
      url: `my/processes/${processId}`,
      method: 'get',
    });
    const res = await (await response()).json();

    if (res.status?.statuskey === 'stop' && res.status?.error) {
      const errorMessage = res.status?.statusmessage;
      if (['TIMEOUT', 'NO_MORE_ATTEMPTS', 'USER_NOT_FOUND'].includes(errorMessage)) {
        verificationStatus.value[type] = {
          ...verificationStatus.value[type],
          processError: errorMessage,
          noMoreAttempts: errorMessage === 'NO_MORE_ATTEMPTS',
        };
        return { ok: false, error: errorMessage };
      }
    }
    return { ok: true };
  } catch (error) {
    console.error(`Error checking process status for ${type}:`, error);
    return { ok: true };
  }
};

const checkVerificationStatus = async (type) => {
  const processId = verificationProcessIds.value[type];
  if (!processId) return null;

  verificationStatus.value[type].isChecking = true;

  try {
    const { response } = hHttpRequest({
      url: `my/processes/${processId}/output`,
      method: 'get',
    });
    const res = await (await response()).json();

    if (res.status?.statuskey === 'stop' && res.status?.error) {
      if (res.status?.statusmessage === 'NO_MORE_ATTEMPTS') {
        verificationStatus.value[type] = {
          ...verificationStatus.value[type],
          verified: false,
          hasMoreAttempts: false,
          noMoreAttempts: true,
          numberOfAttemptsLeft: 0,
          isChecking: false,
          error: 'NO_MORE_ATTEMPTS',
        };
        return { noMoreAttempts: true };
      }
    }

    if (res.output) {
      const output = res.output;
      if (output.verified === true) {
        verificationStatus.value[type] = {
          ...verificationStatus.value[type],
          verified: true,
          hasMoreAttempts: true,
          noMoreAttempts: false,
          numberOfAttemptsLeft: null,
          isChecking: false,
          error: null,
        };
        clearStoredProcessId(type);
        verificationProcessIds.value[type] = '';
        return { verified: true, runCustomProcess: output.runCustomProcess || false };
      }
      if (output.verified === false) {
        const attemptsLeft = output.numberOfAttemptsLeft ?? null;
        const noMoreAttempts = output.hasMoreAttempts === false || attemptsLeft === 0;
        verificationStatus.value[type] = {
          ...verificationStatus.value[type],
          verified: false,
          hasMoreAttempts: output.hasMoreAttempts ?? true,
          noMoreAttempts,
          numberOfAttemptsLeft: attemptsLeft,
          isChecking: false,
          error: null,
        };
        return {
          verified: false,
          numberOfAttemptsLeft: attemptsLeft,
          hasMoreAttempts: output.hasMoreAttempts,
        };
      }
    }

    verificationStatus.value[type].isChecking = false;
    return null;
  } catch (error) {
    console.error(`Error checking verification status for ${type}:`, error);
    verificationStatus.value[type].isChecking = false;
    verificationStatus.value[type].error = error.message;
    return null;
  }
};

const pollVerificationStatus = async (type, maxAttempts = 10, initialDelay = 500) => {
  let delay = initialDelay;
  const previousAttempts = verificationStatus.value[type]?.numberOfAttemptsLeft;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, delay));
    const status = await checkVerificationStatus(type);

    if (status?.verified === true) return status;
    if (status?.noMoreAttempts || status?.hasMoreAttempts === false) return status;
    if (status?.verified === false && typeof status?.numberOfAttemptsLeft === 'number') {
      if (previousAttempts === null || status.numberOfAttemptsLeft < previousAttempts) {
        return status;
      }
    }
    delay = Math.min(delay * 1.4, 1500);
  }

  console.warn(`Polling timed out after ${maxAttempts} attempts for ${type}`);
  return await checkVerificationStatus(type);
};

const pollForVerificationRequired = async (processId, maxAttempts = 10, intervalMs = 500) => {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const { response } = hHttpRequest({
        url: `my/processes/${processId}/output`,
        method: 'get',
      });
      const res = await (await response()).json();

      if (res.status?.error) {
        return { error: res.status?.statusmessage || 'UNKNOWN_ERROR' };
      }
      if (res.output && typeof res.output.verificationRequired !== 'undefined') {
        return { verificationRequired: res.output.verificationRequired, output: res.output };
      }
      if (i < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
    } catch (error) {
      console.error(`Poll attempt ${i + 1} failed:`, error);
      if (i === maxAttempts - 1) throw error;
    }
  }
  return { timeout: true };
};

/**
 * Poll process output until the custom process completes (runCustomProcess flag is cleared).
 */
const pollForCustomProcessCompletion = async (processId, maxAttempts = 60, intervalMs = 2000) => {
  for (let i = 0; i < maxAttempts; i++) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    try {
      const { response } = hHttpRequest({
        url: `my/processes/${processId}/output`,
        method: 'get',
      });
      const res = await (await response()).json();

      if (res.status?.statuskey === 'stop' && res.status?.error) {
        return { error: res.status?.statusmessage || 'CUSTOM_PROCESS_ERROR' };
      }

      if (res.output && !res.output.runCustomProcess) {
        return { done: true, output: res.output };
      }
    } catch (error) {
      console.error(`Custom process poll attempt ${i + 1} failed:`, error);
      if (i === maxAttempts - 1) return { error: 'POLL_FAILED' };
    }
  }
  return { timeout: true };
};

const startNewVerificationProcess = async (type) => {
  const newValue = type === 'email' ? editedEmail.value : editedPhone.value;

  verificationStatus.value[type] = createDefaultVerificationState();

  try {
    const { response } = hHttpRequest({
      url: 'my/processes/start/set-my-contact-information',
      method: 'post',
      payload: {
        title: `Set Contact Information - ${type === 'email' ? 'Email' : 'Phone'} (Retry)`,
        description: 'Retry verification',
        input: {
          attribute_type: type,
          attribute_value: newValue,
        },
      },
    });

    const res = await (await response()).json();
    const processId = res.id;

    if (processId) {
      verificationProcessIds.value[type] = processId;
      const pollResult = await pollForVerificationRequired(processId);
      if (pollResult.error) throw new Error(pollResult.error);

      const expireAt = await fetchProcessExpireAt(processId);
      storeProcessId(type, processId, expireAt);
      return { processId, expireAt };
    }
    return { processId, expireAt: null };
  } catch (error) {
    console.error('Error starting new verification process:', error);
    throw error;
  }
};

const startVerificationForCurrentValue = async (type, value) => {
  if (!value) return;

  prepareVerificationValue(type, value);
  verificationStatus.value[type] = createDefaultVerificationState();

  const storedProcess = getStoredProcessId(type);
  if (storedProcess?.processId) {
    verificationProcessIds.value[type] = storedProcess.processId;
    setVerificationTimer(type, storedProcess.expireAt);
    await checkProcessStatus(type);
    openVerificationDialogForType(type);
    return;
  }

  try {
    const { processId, expireAt } = await startNewVerificationProcess(type);
    if (!processId) throw new Error('Missing verification process id');

    verificationProcessIds.value[type] = processId;
    setVerificationTimer(type, expireAt);
    openVerificationDialogForType(type);

    hAddAlert({
      type: 'info',
      title: useHTranslate('verification_code_sent'),
      timeout: 5000,
    });
  } catch (error) {
    console.error('Start verification error:', error);
    hAddAlert({
      type: 'error',
      title: useHTranslate('citizen_something_went_wrong'),
    });
  }
};

// --- Contact info items ---

const contactFieldConfig = {
  bank_account: {
    icon: 'mdi:mdi-bank',
    label: useHTranslate('citizen_my_profile_bank_account'),
  },
  mobile_phone: {
    icon: 'mdi:mdi-phone',
    label: useHTranslate('citizen_my_profile_mobile_phone'),
  },
  email: {
    icon: 'mdi:mdi-email',
    label: useHTranslate('citizen_my_profile_email_address'),
  },
};

const defaultContactFields = computed(() => {
  const settingValue = citizen_my_profile?.value?.default_contact_detail_attributes;
  if (!settingValue) return [];
  return settingValue.split(',').map((field) => field.trim());
});

const contactInfoItems = computed(() => {
  const contactDetails = oUser.value?.custom?.citizen?.profile_information?.contact_details || {};

  const fieldsToShow = defaultContactFields.value.length > 0 ? defaultContactFields.value : Object.keys(contactDetails);

  return fieldsToShow.map((key) => {
    const data = contactDetails[key] || { value: '', verified: false };
    const config = contactFieldConfig[key] || { icon: 'mdi:mdi-help-circle', label: key };

    // Determine the display value: use suggested value if provided, otherwise current
    const suggestedValue = props.suggestedValues?.[key] || null;
    const currentValue = data.value || '';
    const displayValue = suggestedValue || currentValue;
    const hasValue = !!displayValue;

    // Determine if the field should show as "unverified":
    // - If a suggested value is provided and differs from the current value → always unverified
    // - If a suggested value matches the current value → use actual verification status
    // - If no suggested value → use actual verification status
    const hasSuggestionOverride = suggestedValue && suggestedValue !== currentValue;
    const isVerified = hasSuggestionOverride ? false : !!data.verifiedAt;
    const verificationApplies = key !== 'bank_account';

    const actions = [];

    if (!props.readOnly) {
      // Show "Verifiera" button if the field has a value and is not verified
      if (hasValue && !isVerified && (key === 'email' || key === 'mobile_phone')) {
        actions.push({
          text: useHTranslate('citizen_my_profile_verify'),
          type: 'secondary',
          icon: 'mdi:mdi-check-circle-outline',
          onClick: async () => {
            await startVerificationForCurrentValue(key, displayValue);
          },
        });
      } else if (verificationApplies && data.refreshRequired && hasValue && isVerified && !confirmedFields.value.has(key)) {
        // Show "Bekräfta" only when field IS verified but needs periodic re-confirmation
        actions.push({
          text: useHTranslate('citizen_profile_modal_confirm'),
          type: 'secondary',
          icon: 'mdi:mdi-check-decagram',
          onClick: async () => {
            if (confirmLoading.value[key]) return;
            await confirmContactDetail(key, displayValue);
          },
        });
      }

      // "Ändra" always last (rightmost)
      actions.push({
        text: useHTranslate('citizen_my_profile_change'),
        type: 'primary',
        icon: 'mdi:mdi-pencil',
        onClick: () => {
          if (key === 'email') {
            editedEmail.value = displayValue;
            originalEmail.value = currentValue;
            emailDialogVisible.value = true;
          } else if (key === 'mobile_phone') {
            editedPhone.value = displayValue;
            originalPhone.value = currentValue;
            phoneDialogVisible.value = true;
          } else if (key === 'bank_account') {
            editedBankAccount.value = displayValue;
            originalBankAccount.value = currentValue;
            bankAccountDialogVisible.value = true;
          }
        },
      });
    }

    const isRequired = props.requiredFields.includes(key);
    const showRefreshChip = verificationApplies && data.refreshRequired && hasValue && isVerified && !confirmedFields.value.has(key);

    return {
      label: isRequired ? `${config.label} *` : config.label,
      value: displayValue || '-',
      icon: config.icon,
      ...(verificationApplies && hasValue && !isVerified
        ? {
            chip: {
              text: useHTranslate('citizen_my_profile_not_verified'),
              color: 'warning-lighten-1',
              textColor: '#ffffff',
              icon: 'mdi:mdi-alert',
            },
          }
        : {}),
      ...(showRefreshChip
        ? {
            chip: {
              text: useHTranslate('citizen_profile_confirm_chip'),
              color: 'info-lighten-4',
              textColor: '#1a5276',
              icon: 'mdi:mdi-help-circle',
            },
          }
        : {}),
      actions,
      _fieldKey: key,
      _rawData: data,
    };
  });
});

// --- Save functions ---

const saveBankAccount = async () => {
  if (props.readOnly || bankAccountSaveLoading.value || isBankAccountUnchanged.value || !isValidBankAccount(editedBankAccount.value))
    return;
  bankAccountSaveLoading.value = true;
  try {
    const { response } = hHttpRequest({
      url: 'my/processes/start/set-my-contact-information',
      method: 'post',
      payload: {
        title: 'Set Contact Information - Bank Account',
        description: 'Set bank account contact information',
        input: { attribute_type: 'bank_account', attribute_value: editedBankAccount.value.trim() },
      },
    });
    let result = await (await response()).json();
    if (result.status?.error || !result.id) throw new Error('BANK_ACCOUNT_SAVE_FAILED');
    const processId = result.id;

    // Starting a process (or disabling verification) does not confirm that the user PATCH succeeded.
    for (let attempt = 0; result.status?.statuskey !== 'complete'; attempt++) {
      if (result.status?.statuskey === 'stop') throw new Error('BANK_ACCOUNT_SAVE_FAILED');
      if (attempt === 30) throw new Error('BANK_ACCOUNT_SAVE_TIMEOUT');
      if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 1000));
      const { response: outputResponse } = hHttpRequest({ url: `my/processes/${processId}/output`, method: 'get' });
      result = await (await outputResponse()).json();
      if (result.status?.error) throw new Error('BANK_ACCOUNT_SAVE_FAILED');
    }

    await oRefetchUserSettings();
    bankAccountDialogVisible.value = false;
    hAddAlert({ type: 'success', title: useHTranslate('citizen_my_profile_bank_account_saved'), timeout: 5000 });
  } catch (error) {
    hAddAlert({
      type: 'error',
      title: useHTranslate('citizen_something_went_wrong'),
      body: useHTranslate(error.message === 'BANK_ACCOUNT_SAVE_TIMEOUT' ? 'citizen_timeout' : 'citizen_could_not_save_info'),
      timeout: 5000,
    });
  } finally {
    bankAccountSaveLoading.value = false;
  }
};

const confirmLoading = ref({});

const confirmContactDetail = async (fieldKey, currentValue) => {
  confirmLoading.value[fieldKey] = true;
  try {
    const { response } = hHttpRequest({
      url: 'my/processes/start/confirm-my-contact-information',
      method: 'post',
      payload: {
        title: `Confirm Contact Information - ${fieldKey}`,
        description: `Confirm ${fieldKey} contact information`,
        input: {
          attribute_type: fieldKey,
          attribute_value: currentValue,
        },
      },
    });

    const res = await (await response()).json();

    if (res.status?.error) {
      hAddAlert({
        type: 'error',
        title: useHTranslate('citizen_something_went_wrong'),
        body: res.status?.statusmessage ?? '',
        timeout: 5000,
      });
      return;
    }

    confirmedFields.value.add(fieldKey);
    hAddAlert({ type: 'success', title: useHTranslate('citizen_profile_confirmed_success'), timeout: 3000 });
    await oRefetchUserSettings();
  } catch (error) {
    console.error('Confirm contact detail error:', error);
    hAddAlert({ type: 'error', title: useHTranslate('citizen_something_went_wrong') });
  } finally {
    confirmLoading.value[fieldKey] = false;
  }
};

const saveEmail = async () => {
  emailSaveLoading.value = true;
  const fieldType = 'email';

  try {
    const { response } = hHttpRequest({
      url: 'my/processes/start/set-my-contact-information',
      method: 'post',
      payload: {
        title: 'Set Contact Information - Email',
        description: 'Set email contact information',
        input: {
          attribute_type: fieldType,
          attribute_value: editedEmail.value,
        },
      },
    });

    const res = await (await response()).json();

    if (res.status?.error) {
      hAddAlert({
        type: 'error',
        title: useHTranslate('citizen_something_went_wrong'),
        body: res.status?.statusmessage ?? useHTranslate('citizen_could_not_save_info'),
        timeout: 5000,
      });
      return;
    }

    const processId = res.id;
    const pollResult = await pollForVerificationRequired(processId);

    if (pollResult.error) {
      hAddAlert({ type: 'error', title: useHTranslate('citizen_something_went_wrong'), body: pollResult.error, timeout: 5000 });
      return;
    }
    if (pollResult.timeout) {
      hAddAlert({
        type: 'error',
        title: useHTranslate('citizen_something_went_wrong'),
        body: useHTranslate('citizen_timeout'),
        timeout: 5000,
      });
      return;
    }

    if (pollResult.verificationRequired === true) {
      const expireAt = await fetchProcessExpireAt(processId);
      verificationProcessIds.value[fieldType] = processId;
      storeProcessId(fieldType, processId, expireAt);
      verificationStatus.value[fieldType] = createDefaultVerificationState();

      if (expireAt) {
        const now = new Date();
        const expireDate = new Date(expireAt);
        timeRemaining.value = Math.max(0, Math.floor((expireDate - now) / 1000));
      }

      emailDialogVisible.value = false;
      verificationDialogVisible.value = true;
      hAddAlert({ type: 'info', title: useHTranslate('verification_code_sent'), timeout: 5000 });
    } else {
      emailDialogVisible.value = false;
      hAddAlert({ type: 'success', title: useHTranslate('email_verified_saved'), timeout: 5000 });
      await oRefetchUserSettings();
    }
  } catch (error) {
    console.error('Save email error:', error);
    hAddAlert({ type: 'error', title: useHTranslate('citizen_something_went_wrong') });
  } finally {
    emailSaveLoading.value = false;
  }
};

const savePhone = async () => {
  phoneSaveLoading.value = true;
  const fieldType = 'mobile_phone';

  try {
    const { response } = hHttpRequest({
      url: 'my/processes/start/set-my-contact-information',
      method: 'post',
      payload: {
        title: 'Set Contact Information - Phone',
        description: 'Set phone contact information',
        input: {
          attribute_type: fieldType,
          attribute_value: editedPhone.value,
        },
      },
    });

    const res = await (await response()).json();

    if (res.status?.error) {
      hAddAlert({
        type: 'error',
        title: useHTranslate('citizen_something_went_wrong'),
        body: res.status?.statusmessage ?? useHTranslate('citizen_could_not_save_info'),
        timeout: 5000,
      });
      return;
    }

    const processId = res.id;
    const pollResult = await pollForVerificationRequired(processId);

    if (pollResult.error) {
      hAddAlert({ type: 'error', title: useHTranslate('citizen_something_went_wrong'), body: pollResult.error, timeout: 5000 });
      return;
    }
    if (pollResult.timeout) {
      hAddAlert({
        type: 'error',
        title: useHTranslate('citizen_something_went_wrong'),
        body: useHTranslate('citizen_timeout'),
        timeout: 5000,
      });
      return;
    }

    if (pollResult.verificationRequired === true) {
      const expireAt = await fetchProcessExpireAt(processId);
      verificationProcessIds.value[fieldType] = processId;
      storeProcessId(fieldType, processId, expireAt);
      verificationStatus.value[fieldType] = createDefaultVerificationState();

      if (expireAt) {
        const now = new Date();
        const expireDate = new Date(expireAt);
        phoneTimeRemaining.value = Math.max(0, Math.floor((expireDate - now) / 1000));
      }

      phoneDialogVisible.value = false;
      phoneVerificationDialogVisible.value = true;
      hAddAlert({ type: 'info', title: useHTranslate('verification_code_sent'), timeout: 5000 });
    } else {
      phoneDialogVisible.value = false;
      hAddAlert({ type: 'success', title: useHTranslate('phone_saved'), timeout: 5000 });
      await oRefetchUserSettings();
    }
  } catch (error) {
    console.error('Save phone error:', error);
    hAddAlert({ type: 'error', title: useHTranslate('citizen_something_went_wrong') });
  } finally {
    phoneSaveLoading.value = false;
  }
};

// --- Verification code submission ---

const submitVerificationCode = async (type) => {
  const code = type === 'email' ? otpCode.value : phoneOtpCode.value;
  const fieldType = type === 'email' ? 'email' : 'mobile_phone';
  const processId = verificationProcessIds.value[fieldType];

  if (type === 'email') {
    emailVerificationLoading.value = true;
  } else {
    phoneVerificationLoading.value = true;
  }

  if (!code || code.length !== 6) {
    hAddAlert({ type: 'warning', title: useHTranslate('citizen_enter_valid_code') });
    if (type === 'email') emailVerificationLoading.value = false;
    else phoneVerificationLoading.value = false;
    return;
  }

  if (!processId) {
    hAddAlert({ type: 'error', title: useHTranslate('citizen_something_went_wrong') });
    if (type === 'email') emailVerificationLoading.value = false;
    else phoneVerificationLoading.value = false;
    return;
  }

  const processStatus = await checkProcessStatus(fieldType);
  if (!processStatus.ok) {
    if (type === 'email') emailVerificationLoading.value = false;
    else phoneVerificationLoading.value = false;
    return;
  }

  try {
    const { response } = hHttpRequest({
      url: `my/processes/${processId}/signal/receiveValidationCode`,
      method: 'put',
      payload: { inputValidationCode: code },
    });
    await (await response()).json();

    const status = await pollVerificationStatus(fieldType);

    if (status?.verified === true) {
      if (type === 'email') {
        otpCode.value = '';
        hAddAlert({ type: 'success', title: useHTranslate('verification_code_verified'), timeout: 5000 });
        setTimeout(() => {
          verificationDialogVisible.value = false;
        }, 1500);
      } else {
        phoneOtpCode.value = '';
        hAddAlert({ type: 'success', title: useHTranslate('verification_phone_verified') });
        setTimeout(() => {
          phoneVerificationDialogVisible.value = false;
        }, 1500);
      }

      // If a custom process is running (e.g. TOPdesk sync + case indexing), show loader and wait
      if (status.runCustomProcess) {
        customProcessLoading.value = true;
        try {
          const result = await pollForCustomProcessCompletion(processId);
          if (result.error) {
            console.error('Custom process error:', result.error);
            hAddAlert({ type: 'error', title: useHTranslate('citizen_something_went_wrong'), timeout: 5000 });
          } else if (result.timeout) {
            console.warn('Custom process polling timed out');
            hAddAlert({ type: 'warning', title: useHTranslate('citizen_something_went_wrong'), timeout: 5000 });
          }
        } catch (error) {
          console.error('Custom process polling failed:', error);
          hAddAlert({ type: 'error', title: useHTranslate('citizen_something_went_wrong') });
        } finally {
          customProcessLoading.value = false;
        }
      }

      clearStoredProcessId(fieldType);
      verificationProcessIds.value[fieldType] = '';
      await oRefetchUserSettings();
    } else if (status?.noMoreAttempts) {
      hAddAlert({ type: 'error', title: useHTranslate('verification_no_more_attempts'), timeout: 5000 });
    } else if (status?.hasMoreAttempts === true && status?.numberOfAttemptsLeft !== undefined) {
      hAddAlert({
        type: 'warning',
        title: useHTranslate('verification_wrong_code'),
        body: useHTranslate('verification_attempts_remaining', { count: status.numberOfAttemptsLeft }),
        timeout: 5000,
      });
    } else if (status?.verified === false) {
      hAddAlert({ type: 'warning', title: useHTranslate('verification_wrong_code'), timeout: 5000 });
    }
  } catch (error) {
    console.error('Verify code error:', error);
    hAddAlert({ type: 'error', title: useHTranslate('citizen_something_went_wrong') });
  } finally {
    if (type === 'email') emailVerificationLoading.value = false;
    else phoneVerificationLoading.value = false;
  }
};

// --- Retry verification ---

const retryVerification = async (type) => {
  if (type === 'email') emailVerificationLoading.value = true;
  else phoneVerificationLoading.value = true;

  try {
    clearStoredProcessId(type);
    verificationProcessIds.value[type] = '';
    verificationStatus.value[type] = createDefaultVerificationState();

    const { expireAt } = await startNewVerificationProcess(type);

    if (expireAt) {
      const now = new Date();
      const expireDate = new Date(expireAt);
      const remainingSeconds = Math.max(0, Math.floor((expireDate - now) / 1000));
      if (type === 'email') {
        timeRemaining.value = remainingSeconds;
        if (timerRef.value) timerRef.value.restart();
      } else {
        phoneTimeRemaining.value = remainingSeconds;
        if (phoneTimerRef.value) phoneTimerRef.value.restart();
      }
    }

    if (type === 'email') otpCode.value = '';
    else phoneOtpCode.value = '';

    hAddAlert({ type: 'info', title: useHTranslate('verification_new_code_sent'), timeout: 5000 });
  } catch (error) {
    console.error('Retry verification error:', error);
    hAddAlert({ type: 'error', title: useHTranslate('citizen_something_went_wrong') });
  } finally {
    if (type === 'email') emailVerificationLoading.value = false;
    else phoneVerificationLoading.value = false;
  }
};

// --- Dialog handlers ---

const handleOtpComplete = () => {};
const handlePhoneOtpComplete = () => {};

const handleEmailSaveClick = () => {
  if (verificationStatus.value.email.processError || verificationStatus.value.email.noMoreAttempts) {
    retryVerification('email');
  } else {
    submitVerificationCode('email');
  }
};

const handlePhoneSaveClick = () => {
  if (verificationStatus.value.mobile_phone.processError || verificationStatus.value.mobile_phone.noMoreAttempts) {
    retryVerification('mobile_phone');
  } else {
    submitVerificationCode('phone');
  }
};

const resendCode = async () => {
  const processId = verificationProcessIds.value.email;
  if (processId) {
    const expireAt = await fetchProcessExpireAt(processId);
    if (expireAt) {
      const now = new Date();
      const expireDate = new Date(expireAt);
      timeRemaining.value = Math.max(0, Math.floor((expireDate - now) / 1000));
    }
  }
  if (timerRef.value) timerRef.value.restart();
  hAddAlert({ type: 'info', title: useHTranslate('verification_code_resent') });
};

const resendPhoneCode = async () => {
  const processId = verificationProcessIds.value.mobile_phone;
  if (processId) {
    const expireAt = await fetchProcessExpireAt(processId);
    if (expireAt) {
      const now = new Date();
      const expireDate = new Date(expireAt);
      phoneTimeRemaining.value = Math.max(0, Math.floor((expireDate - now) / 1000));
    }
  }
  if (phoneTimerRef.value) phoneTimerRef.value.restart();
  hAddAlert({ type: 'info', title: useHTranslate('verification_phone_code_resent'), timeout: 5000 });
};

const handleTimerComplete = () => {
  verificationStatus.value.email.processError = 'TIMEOUT';
  hAddAlert({ type: 'warning', title: useHTranslate('verification_time_expired') });
};

const handlePhoneTimerComplete = () => {
  verificationStatus.value.mobile_phone.processError = 'TIMEOUT';
  hAddAlert({ type: 'warning', title: useHTranslate('verification_time_expired'), timeout: 5000 });
};

const closeEmailVerificationDialog = () => {
  verificationDialogVisible.value = false;
  if (verificationStatus.value.email.verified) {
    otpCode.value = '';
    verificationStatus.value.email = createDefaultVerificationState();
  }
};

const closePhoneVerificationDialog = () => {
  phoneVerificationDialogVisible.value = false;
  if (verificationStatus.value.mobile_phone.verified) {
    phoneOtpCode.value = '';
    verificationStatus.value.mobile_phone = createDefaultVerificationState();
  }
};

// --- Init ---

const initVerificationProcesses = () => {
  const emailData = getStoredProcessId('email');
  const phoneData = getStoredProcessId('mobile_phone');
  if (emailData) verificationProcessIds.value.email = emailData.processId;
  if (phoneData) verificationProcessIds.value.mobile_phone = phoneData.processId;
};

initVerificationProcesses();

// Expose for parent to call
defineExpose({});
</script>

<style>
/* Child dialogs inside profile modal need higher z-index than the modal itself (2500) */
.profile-child-dialog--elevated.v-overlay {
  z-index: 2600 !important;
}
.v-overlay:has(.profile-child-dialog--elevated) {
  z-index: 2600 !important;
}
.profile-resend-code {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  color: inherit;
}
</style>
